import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import store from "store";
import AuthActions from "Redux/AuthRedux";

// Storage keys
const AUTH_DATA_KEY = "authData";
const USER_DATA_KEY = "userData";
const TEAM_KEY = "video_panda_team";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  
  // Get user from localStorage
  const [user, setUser] = useState(() => store.get(USER_DATA_KEY) || null);
  const [currentTeamId, setCurrentTeamId] = useState(() => store.get(TEAM_KEY) || null);

  // Sync user from localStorage when it changes
  useEffect(() => {
    const handleStorageChange = () => {
      setUser(store.get(USER_DATA_KEY) || null);
      setCurrentTeamId(store.get(TEAM_KEY) || null);
    };

    // Listen for storage changes
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Sign Out - following appointo_client pattern
  const signOut = useCallback(() => {
    return new Promise((resolve) => {
      dispatch(
        AuthActions.signOutRequest(() => {
          setUser(null);
          setCurrentTeamId(null);
          resolve();
        })
      );
    });
  }, [dispatch]);

  // Switch Team
  const switchTeam = useCallback(
    (teamId) => {
      dispatch(
        AuthActions.switchTeam(teamId.toString(), () => {
          setCurrentTeamId(teamId);
        })
      );
    },
    [dispatch]
  );

  // Refresh current user
  const refreshUser = useCallback(() => {
    return new Promise((resolve, reject) => {
      dispatch(
        AuthActions.meRequest((resp, error) => {
          if (error) {
            reject(new Error(error));
          } else {
            setUser(store.get(USER_DATA_KEY));
            resolve(resp);
          }
        })
      );
    });
  }, [dispatch]);

  const teams = user?.teams || [];
  const isAuthenticated = !!store.get(AUTH_DATA_KEY)?.token && !!user;

  const value = {
    user,
    teams,
    currentTeamId,
    isAuthenticated,
    signOut,
    switchTeam,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
